import { useEffect, useState } from 'react';
import { Box, Drawer, Typography } from '@mui/material';
import { Spacer } from '.';

export interface RightDrawerProps extends React.PropsWithChildren {
  open: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  onClose?: () => void;
  contentStyles?: React.CSSProperties;
  smallTitle?: string;
  title?: string;
  footer?: React.ReactNode;
  footerShadow?: boolean;
}

const RightDrawer: React.FC<RightDrawerProps> = props => {
  const {
    open,
    setOpen,
    onClose,
    smallTitle,
    title,
    footer,
    footerShadow,
    contentStyles,
  } = props;
  const [scrollableContent, setScrollableContent] = useState<HTMLDivElement | null>(null);
  const [innerContent, setInnerContent] = useState<HTMLDivElement | null>(null);
  const [canScrollDown, setCanScrollDown] = useState(false);

  useEffect(() => {
    if (!scrollableContent || !innerContent || !footerShadow) return;

    const updateCanScrollDown = () => {
      const { scrollHeight, scrollTop, clientHeight } = scrollableContent;
      setCanScrollDown(scrollHeight > scrollTop + clientHeight + 1);
    };

    updateCanScrollDown();
    scrollableContent.addEventListener('scroll', updateCanScrollDown, { passive: true });

    const resizeObserver = new ResizeObserver(updateCanScrollDown);
    resizeObserver.observe(innerContent);

    return () => {
      scrollableContent.removeEventListener('scroll', updateCanScrollDown);
      resizeObserver.disconnect();
    };
  }, [scrollableContent, innerContent, footerShadow]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    setOpen?.(false);
  };

  return (
    <Drawer
      sx={{
        'zIndex': `${20} !important` /* if you touch this, make sure you haven't broken the edit org info drawer */,
        '& .MuiDrawer-paper': {
          overflow: 'auto',
        },
      }}
      anchor='right'
      open={open}
      onClose={handleClose}>
      <Box
        role='presentation'
        sx={{
          width: '700px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Box
          ref={setScrollableContent}
          sx={{
            padding: '20px',
            flex: 1,
            overflow: 'auto',
            ...contentStyles,
          }}>
          {smallTitle ? (
            <>
              <Typography color='info.main' variant='p14'>
                {smallTitle}
              </Typography>
              <Spacer height='xxs' />
            </>
          ) : null}
          {title ? (
            <>
              <Typography color='secondary.main' variant='p30'>
                {title}
              </Typography>
              <Spacer height='xxs' />
            </>
          ) : null}
          <div ref={setInnerContent}>{props.children}</div>
        </Box>
        {footer && (
          <Box
            sx={{
              padding: '20px',
              borderTop: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
              backgroundColor: 'background.paper',
              boxShadow: footerShadow && canScrollDown ? '0px -4px 8px rgba(0, 0, 0, 0.1)' : 'none',
              transition: 'box-shadow 0.2s ease',
            }}>
            {footer}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default RightDrawer;
